import type {
  CeilingFinish,
  CeilingFinishId,
  FloorFinish,
  FloorFinishId,
  RoomSetting,
  WallFinish,
  WallFinishId,
} from "../types";
import type { SurfaceId } from "../render3d/materials/catalog3d";
import { hexToRgb } from "../furniture/color";

/**
 * Every floor, wall and ceiling finish a room can have, the colors each one
 * comes in, and the curated Looks. Pure data (no three): the Finishes sheet
 * renders it, the 3D resolver maps it onto `catalog3d` surfaces.
 */

export type FinishSurfaceKind = "floor" | "walls" | "ceiling";

export interface FinishSwatch {
  /** Stable id; analytics report this, never the hex. */
  id: string;
  name: string;
  hex: string;
}

/** How a finish is built in 3D beyond a textured face. */
export type FinishGeometry =
  | "flat"
  | "slat"
  | "panelling"
  | "beams"
  | "fence"
  | "railing"
  | "hedge"
  | "stucco"
  | "house"
  | "open"
  | "pergola"
  | "covered";

export interface FinishOption<T extends string = string> {
  id: T;
  label: string;
  group: string;
  /** 3D surface the finish renders with. */
  surface: SurfaceId;
  /** Surface used when the color isn't the scan's own (painted brick). */
  paintedSurface?: SurfaceId;
  /** Texture set whose scanned crop is the picker tile (`swatches.generated.ts`). */
  thumb: string | null;
  swatches: readonly FinishSwatch[];
  /** Pattern drawn over the tile for geometry finishes. */
  geometry: FinishGeometry;
  /** Planks / boards that can run either way. */
  directional?: boolean;
  /** Outdoor boundary height in inches; missing = the room's ceiling height. */
  height?: number;
  settings: readonly RoomSetting[];
}

const sw = (id: string, name: string, hex: string): FinishSwatch => ({ id, name, hex });

const INDOOR = ["indoor"] as const;
const OUTDOOR = ["outdoor"] as const;
const BOTH = ["indoor", "outdoor"] as const;

export const PAINT_SWATCHES: readonly FinishSwatch[] = [
  sw("warm-white", "Warm white", "#ece8e1"),
  sw("bright-white", "Bright white", "#f4f3ef"),
  sw("greige", "Greige", "#d6cfc4"),
  sw("sage", "Sage", "#b9c2ae"),
  sw("dusty-blue", "Dusty blue", "#a9b8c4"),
  sw("terracotta", "Terracotta", "#c98f72"),
  sw("navy", "Navy", "#2f3c52"),
  sw("charcoal", "Charcoal", "#4a4a48"),
];

const WOOD_TONES: readonly FinishSwatch[] = [
  sw("natural-oak", "Natural oak", "#b48a5e"),
  sw("white-oak", "White oak", "#cdb48f"),
  sw("walnut", "Walnut", "#6b4a33"),
  sw("black", "Black", "#2b2a28"),
];

export const TRIM_SWATCHES: readonly FinishSwatch[] = [
  sw("soft-white", "Soft white", "#f5f3ee"),
  sw("greige", "Greige", "#d6cfc4"),
  sw("sage", "Sage", "#b9c2ae"),
  sw("navy", "Navy", "#2f3c52"),
  sw("charcoal", "Charcoal", "#4a4a48"),
  sw("black", "Black", "#232322"),
];

export const FLOOR_FINISHES: readonly FinishOption<FloorFinishId>[] = [
  {
    id: "oak",
    label: "Oak",
    group: "Wood",
    surface: "floor-oak",
    thumb: "floor-oak",
    geometry: "flat",
    directional: true,
    settings: INDOOR,
    swatches: [
      sw("natural", "Natural", "#806043"),
      sw("honey", "Honey", "#a87a4c"),
      sw("gray-wash", "Gray wash", "#8c857c"),
      sw("smoked", "Smoked", "#5b4636"),
      sw("ebony", "Ebony", "#3a2d25"),
    ],
  },
  {
    id: "walnut",
    label: "Walnut",
    group: "Wood",
    surface: "floor-walnut",
    thumb: "floor-walnut",
    geometry: "flat",
    directional: true,
    settings: INDOOR,
    swatches: [
      sw("classic", "Classic", "#5e4331"),
      sw("warm", "Warm", "#7a5338"),
      sw("smoked", "Smoked", "#4a4038"),
      sw("espresso", "Espresso", "#31271a"),
    ],
  },
  {
    id: "whitewash",
    label: "Whitewashed",
    group: "Wood",
    surface: "floor-whitewash",
    thumb: "floor-whitewash",
    geometry: "flat",
    directional: true,
    settings: INDOOR,
    swatches: [
      sw("whitewash", "Whitewash", "#e4ddd0"),
      sw("natural", "Natural maple", "#e0c59c"),
      sw("ash", "Ash", "#cbc3b6"),
      sw("sand", "Sand", "#d8c4a3"),
    ],
  },
  {
    id: "herringbone",
    label: "Herringbone",
    group: "Wood",
    surface: "floor-herringbone",
    thumb: "floor-herringbone",
    geometry: "flat",
    directional: true,
    settings: INDOOR,
    swatches: [
      sw("natural", "Natural", "#917258"),
      sw("honey", "Honey", "#a87a4c"),
      sw("whitewash", "Whitewash", "#d9d0c1"),
      sw("smoked", "Smoked", "#5b4636"),
    ],
  },
  {
    id: "porcelain",
    label: "Porcelain tile",
    group: "Stone & tile",
    surface: "floor-porcelain",
    thumb: "floor-porcelain",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("natural", "Sand", "#ac9986"),
      sw("warm-white", "Warm white", "#e3ddd2"),
      sw("light-gray", "Light gray", "#bdbab4"),
      sw("charcoal", "Charcoal", "#4f4e4c"),
    ],
  },
  {
    id: "stone",
    label: "Stone",
    group: "Stone & tile",
    surface: "floor-stone",
    thumb: "floor-stone",
    geometry: "flat",
    settings: BOTH,
    swatches: [
      sw("natural", "Pale gray", "#cdcdcd"),
      sw("travertine", "Travertine", "#d6c6ab"),
      sw("limestone", "Limestone", "#e0d9cb"),
      sw("slate", "Slate", "#5c6064"),
    ],
  },
  {
    id: "terrazzo",
    label: "Terrazzo",
    group: "Stone & tile",
    surface: "floor-terrazzo",
    thumb: "terrazzo",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("natural", "Natural", "#dbd6d0"),
      sw("blush", "Blush", "#e3c9bd"),
      sw("sage", "Sage", "#c4ccbc"),
      sw("charcoal", "Charcoal", "#66635f"),
    ],
  },
  {
    id: "carpet",
    label: "Carpet",
    group: "Soft",
    surface: "floor-carpet",
    thumb: "carpet",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("oatmeal", "Oatmeal", "#cbbfae"),
      sw("greige", "Greige", "#b5ab9d"),
      sw("slate-blue", "Slate blue", "#6f7d8c"),
      sw("moss", "Moss", "#7c8466"),
      sw("charcoal", "Charcoal", "#4e4d4b"),
    ],
  },
  {
    id: "concrete",
    label: "Concrete",
    group: "Industrial",
    surface: "floor-concrete",
    thumb: "concrete",
    geometry: "flat",
    settings: BOTH,
    swatches: [
      sw("light-gray", "Light gray", "#bdbab3"),
      sw("warm-gray", "Warm gray", "#a59f95"),
      sw("polished-white", "Polished white", "#d9d6cf"),
      sw("charcoal", "Charcoal", "#5d5c59"),
    ],
  },
  {
    id: "deck",
    label: "Deck",
    group: "Hard",
    surface: "ground-deck",
    thumb: "deck",
    geometry: "flat",
    directional: true,
    settings: OUTDOOR,
    swatches: [
      sw("teak", "Teak", "#9a7652"),
      sw("redwood", "Redwood", "#713712"),
      sw("weathered", "Weathered gray", "#8f8a82"),
      sw("espresso", "Espresso", "#3e2f25"),
    ],
  },
  {
    id: "pavers",
    label: "Pavers",
    group: "Hard",
    surface: "ground-pavers",
    thumb: "pavers",
    geometry: "flat",
    settings: OUTDOOR,
    swatches: [
      sw("natural", "Natural", "#a29d90"),
      sw("sandstone", "Sandstone", "#c9b79a"),
      sw("charcoal", "Charcoal", "#5b5a58"),
    ],
  },
  {
    id: "grass",
    label: "Grass",
    group: "Natural",
    surface: "ground-grass",
    thumb: "grass",
    geometry: "flat",
    settings: OUTDOOR,
    swatches: [
      sw("natural", "Natural", "#647034"),
      sw("lush", "Lush", "#4f7a32"),
      sw("dry", "Dry", "#9d9a5c"),
    ],
  },
  {
    id: "gravel",
    label: "Gravel",
    group: "Natural",
    surface: "ground-gravel",
    thumb: "gravel",
    geometry: "flat",
    settings: OUTDOOR,
    swatches: [
      sw("natural", "Pale", "#dbd7cf"),
      sw("pea", "Pea gravel", "#b9a789"),
      sw("slate", "Slate", "#6d6f71"),
    ],
  },
];

export const WALL_FINISHES: readonly FinishOption<WallFinishId>[] = [
  { id: "paint", label: "Paint", group: "Paint & plaster", surface: "wall-paint", thumb: "plaster", geometry: "flat", settings: INDOOR, swatches: PAINT_SWATCHES },
  {
    id: "plaster",
    label: "Limewash",
    group: "Paint & plaster",
    surface: "wall-plaster",
    thumb: "plaster-rough",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("chalk", "Chalk", "#ece6dc"),
      sw("stone", "Stone", "#cfc5b6"),
      sw("clay", "Clay", "#c4a28a"),
      sw("sage", "Sage", "#b8bfae"),
      sw("smoke", "Smoke", "#8e8e8a"),
    ],
  },
  {
    id: "shiplap",
    label: "Shiplap",
    group: "Wood",
    surface: "wall-shiplap",
    thumb: "shiplap",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("bright-white", "Bright white", "#f4f3ef"),
      sw("warm-white", "Warm white", "#ece8e1"),
      sw("greige", "Greige", "#d6cfc4"),
      sw("sage", "Sage", "#b9c2ae"),
      sw("dusty-blue", "Dusty blue", "#a9b8c4"),
      sw("navy", "Navy", "#2f3c52"),
      sw("charcoal", "Charcoal", "#4a4a48"),
    ],
  },
  { id: "slat", label: "Wood slats", group: "Wood", surface: "wall-slat", thumb: "oak", geometry: "slat", settings: INDOOR, swatches: WOOD_TONES },
  {
    id: "panelling",
    label: "Panelling",
    group: "Wood",
    surface: "wall-paint",
    thumb: "painted-wood",
    geometry: "panelling",
    settings: INDOOR,
    swatches: [
      sw("warm-white", "Warm white", "#ece8e1"),
      sw("greige", "Greige", "#d6cfc4"),
      sw("sage", "Sage", "#b9c2ae"),
      sw("dusty-blue", "Dusty blue", "#a9b8c4"),
      sw("navy", "Navy", "#2f3c52"),
      sw("forest", "Forest green", "#34493d"),
      sw("charcoal", "Charcoal", "#4a4a48"),
    ],
  },
  {
    id: "brick",
    label: "Brick",
    group: "Masonry & tile",
    surface: "wall-brick",
    paintedSurface: "wall-brick-painted",
    thumb: "brick",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("natural", "Red brick", "#a27c65"),
      sw("painted-white", "Painted white", "#efece6"),
      sw("painted-greige", "Painted greige", "#d6cfc4"),
      sw("painted-black", "Painted black", "#2e2d2b"),
    ],
  },
  {
    id: "tile",
    label: "Tile",
    group: "Masonry & tile",
    surface: "wall-tile",
    thumb: "wall-tile",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("white", "White", "#f1efea"),
      sw("sage", "Sage", "#b9c2ae"),
      sw("sky", "Sky", "#c9d6de"),
      sw("terracotta", "Terracotta", "#c98f72"),
      sw("ink", "Ink", "#2d3646"),
    ],
  },
  {
    id: "fence-wood",
    label: "Wood fence",
    group: "Fences",
    surface: "fence-wood",
    thumb: "oak",
    geometry: "fence",
    height: 72,
    settings: OUTDOOR,
    swatches: [
      sw("cedar", "Cedar", "#a8774f"),
      sw("weathered", "Weathered gray", "#9a948b"),
      sw("white", "White", "#efede7"),
      sw("black", "Black", "#2b2a28"),
    ],
  },
  { id: "fence-slat", label: "Slat fence", group: "Fences", surface: "fence-wood", thumb: "oak", geometry: "fence", height: 72, settings: OUTDOOR, swatches: WOOD_TONES },
  {
    id: "railing",
    label: "Railing",
    group: "Fences",
    surface: "black-metal",
    thumb: null,
    geometry: "railing",
    height: 42,
    settings: OUTDOOR,
    swatches: [sw("black", "Black", "#2a2a2a"), sw("white", "White", "#eeeeea"), sw("bronze", "Bronze", "#5b4a3a")],
  },
  {
    id: "hedge",
    label: "Hedge",
    group: "Green",
    surface: "hedge",
    thumb: "grass",
    geometry: "hedge",
    height: 60,
    settings: OUTDOOR,
    swatches: [sw("boxwood", "Boxwood", "#4f6b36"), sw("laurel", "Laurel", "#3c5a32"), sw("olive", "Olive", "#6e7a4a")],
  },
  {
    id: "masonry",
    label: "Masonry wall",
    group: "Walls",
    surface: "masonry",
    paintedSurface: "masonry-painted",
    thumb: "masonry",
    geometry: "flat",
    height: 66,
    settings: OUTDOOR,
    swatches: [
      sw("natural", "Concrete block", "#878786"),
      sw("white", "Painted white", "#eeebe5"),
      sw("greige", "Painted greige", "#d6cfc4"),
      sw("charcoal", "Painted charcoal", "#4a4a48"),
    ],
  },
  {
    id: "stucco",
    label: "Stucco",
    group: "Walls",
    surface: "stucco",
    thumb: "stucco",
    geometry: "stucco",
    height: 72,
    settings: OUTDOOR,
    swatches: [
      sw("sand", "Sand", "#e6d3b3"),
      sw("warm-white", "Warm white", "#f3efe6"),
      sw("adobe", "Adobe", "#c4a07a"),
      sw("greige", "Greige", "#c9c0b2"),
      sw("white", "White", "#f7f5f0"),
    ],
  },
  {
    id: "house",
    label: "Neighbor's house",
    group: "Walls",
    surface: "house-siding",
    thumb: "shiplap",
    geometry: "house",
    settings: OUTDOOR,
    swatches: [
      sw("cream", "Cream", "#e8e0cf"),
      sw("gray", "Gray", "#a7a9a6"),
      sw("sage", "Sage", "#a9b39c"),
      sw("navy", "Navy", "#34415a"),
    ],
  },
  { id: "open", label: "Open", group: "Open", surface: "wall-paint", thumb: null, geometry: "open", height: 0, settings: OUTDOOR, swatches: [sw("none", "Nothing", "#d9e4ea")] },
];

export const CEILING_FINISHES: readonly FinishOption<CeilingFinishId>[] = [
  {
    id: "paint",
    label: "Paint",
    group: "Paint",
    surface: "ceiling-paint",
    thumb: "plaster",
    geometry: "flat",
    settings: INDOOR,
    swatches: [
      sw("ceiling-white", "Ceiling white", "#f6f5f2"),
      sw("warm-white", "Warm white", "#ece8e1"),
      sw("sky", "Sky", "#dfe7ec"),
      sw("greige", "Greige", "#d6cfc4"),
      sw("charcoal", "Charcoal", "#4a4a48"),
    ],
  },
  {
    id: "planks",
    label: "Wood planks",
    group: "Wood",
    surface: "ceiling-planks",
    thumb: "ceiling-planks",
    geometry: "flat",
    directional: true,
    settings: INDOOR,
    swatches: [
      sw("natural-pine", "Natural pine", "#c9a57c"),
      sw("whitewashed", "Whitewashed", "#e6dfd2"),
      sw("walnut", "Walnut", "#6b4f3a"),
    ],
  },
  { id: "beams", label: "Beams", group: "Wood", surface: "ceiling-beam", thumb: "oak", geometry: "beams", settings: INDOOR, swatches: WOOD_TONES },
  { id: "open", label: "Open sky", group: "Overhead", surface: "ceiling-paint", thumb: null, geometry: "open", settings: OUTDOOR, swatches: [sw("sky", "Sky", "#cfe0ec")] },
  { id: "pergola", label: "Pergola", group: "Overhead", surface: "pergola-wood", thumb: "oak", geometry: "pergola", settings: OUTDOOR, swatches: WOOD_TONES },
  {
    id: "covered",
    label: "Covered",
    group: "Overhead",
    surface: "ceiling-planks",
    thumb: "ceiling-planks",
    geometry: "covered",
    settings: OUTDOOR,
    swatches: [sw("natural-pine", "Natural pine", "#c9a57c"), sw("white", "White", "#efede7"), sw("charcoal", "Charcoal", "#4a4a48")],
  },
];

export interface Look {
  id: string;
  label: string;
  setting: RoomSetting;
  floor: FloorFinish;
  walls: WallFinish;
  ceiling: CeilingFinish;
  trimColor: string;
}

export const LOOKS: readonly Look[] = [
  {
    id: "scandi",
    label: "Scandi",
    setting: "indoor",
    floor: { type: "whitewash", color: "#e4ddd0" },
    walls: { type: "paint", color: "#f4f3ef" },
    ceiling: { type: "paint", color: "#f6f5f2" },
    trimColor: "#f5f3ee",
  },
  {
    id: "warm-modern",
    label: "Warm modern",
    setting: "indoor",
    floor: { type: "walnut", color: "#5e4331" },
    walls: { type: "plaster", color: "#cfc5b6" },
    ceiling: { type: "paint", color: "#ece8e1" },
    trimColor: "#d6cfc4",
  },
  {
    id: "loft",
    label: "Loft",
    setting: "indoor",
    floor: { type: "concrete", color: "#a59f95" },
    walls: { type: "brick", color: "#a27c65" },
    ceiling: { type: "beams", color: "#2b2a28" },
    trimColor: "#232322",
  },
  {
    id: "coastal",
    label: "Coastal",
    setting: "indoor",
    floor: { type: "oak", color: "#a87a4c" },
    walls: { type: "shiplap", color: "#f4f3ef" },
    ceiling: { type: "paint", color: "#dfe7ec" },
    trimColor: "#f5f3ee",
  },
  {
    id: "spa",
    label: "Spa",
    setting: "indoor",
    floor: { type: "stone", color: "#d6c6ab" },
    walls: { type: "plaster", color: "#b8bfae" },
    ceiling: { type: "paint", color: "#f6f5f2" },
    trimColor: "#f5f3ee",
  },
  {
    id: "garden",
    label: "Garden",
    setting: "outdoor",
    floor: { type: "deck", color: "#9a7652" },
    walls: { type: "fence-wood", color: "#a8774f" },
    ceiling: { type: "pergola", color: "#b48a5e" },
    trimColor: "#f5f3ee",
  },
];

type AnyFinishId = FloorFinishId | WallFinishId | CeilingFinishId;

export function finishOptions(surface: FinishSurfaceKind): readonly FinishOption<AnyFinishId>[] {
  return surface === "floor" ? FLOOR_FINISHES : surface === "walls" ? WALL_FINISHES : CEILING_FINISHES;
}

export function floorFinish(id: FloorFinishId): FinishOption<FloorFinishId> {
  return FLOOR_FINISHES.find((f) => f.id === id) ?? FLOOR_FINISHES[0];
}

export function wallFinish(id: WallFinishId): FinishOption<WallFinishId> {
  return WALL_FINISHES.find((f) => f.id === id) ?? WALL_FINISHES[0];
}

export function ceilingFinish(id: CeilingFinishId): FinishOption<CeilingFinishId> {
  return CEILING_FINISHES.find((f) => f.id === id) ?? CEILING_FINISHES[0];
}

export function finishOption(surface: FinishSurfaceKind, id: string): FinishOption<AnyFinishId> | undefined {
  return finishOptions(surface).find((f) => f.id === id);
}

/** The finishes a surface offers in this setting, catalog order. */
export function finishesFor(surface: FinishSurfaceKind, setting: RoomSetting): FinishOption<AnyFinishId>[] {
  return finishOptions(surface).filter((f) => f.settings.includes(setting));
}

export function isFinishValid(surface: FinishSurfaceKind, id: string, setting: RoomSetting): boolean {
  return !!finishOption(surface, id)?.settings.includes(setting);
}

export function sameHex(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The curated swatch a color is, if any. */
export function swatchFor(option: Pick<FinishOption, "swatches">, hex: string): FinishSwatch | null {
  return option.swatches.find((s) => sameHex(s.hex, hex)) ?? null;
}

/**
 * Color to keep when the type changes: the current color if the new type
 * offers it, else the new type's first swatch (a red brick wall turned to
 * paint shouldn't stay brick red). Custom colors carry over on tintable types.
 */
export function colorForType(prev: { color: string; swatches: readonly FinishSwatch[] }, next: FinishOption): string {
  if (swatchFor(next, prev.color)) return prev.color;
  const wasCurated = !!swatchFor({ swatches: prev.swatches }, prev.color);
  if (!wasCurated && next.swatches.length > 1) return prev.color;
  return next.swatches[0].hex;
}

/** The 3D surface a finish renders with in this color: brick in any color but its own is painted brick. */
export function finishSurfaceFor(option: FinishOption, color: string): SurfaceId {
  if (!option.paintedSurface) return option.surface;
  return swatchFor({ swatches: option.swatches.slice(0, 1) }, color) ? option.surface : option.paintedSurface;
}

/** "Walnut · Classic", or the nearest named color for a custom one. */
export function finishLabel(option: FinishOption, hex: string): string {
  if (option.swatches.length <= 1) return option.label;
  return `${option.label} · ${colorName(option, hex)}`;
}

export function colorName(option: Pick<FinishOption, "swatches">, hex: string): string {
  return swatchFor(option, hex)?.name ?? nearestColorName(hex);
}

/** What analytics reports for a color: the swatch id, or `custom`. */
export function colorId(option: Pick<FinishOption, "swatches">, hex: string): string {
  return swatchFor(option, hex)?.id ?? "custom";
}

export function lookById(id: string | undefined): Look | undefined {
  return id ? LOOKS.find((l) => l.id === id) : undefined;
}

export function looksFor(setting: RoomSetting): Look[] {
  return LOOKS.filter((l) => l.setting === setting);
}

/** The 3D surfaces a Look renders with, to start their textures early. */
export function lookSurfaces(look: Look): SurfaceId[] {
  return [
    finishSurfaceFor(floorFinish(look.floor.type), look.floor.color),
    finishSurfaceFor(wallFinish(look.walls.type), look.walls.color),
    finishSurfaceFor(ceilingFinish(look.ceiling.type), look.ceiling.color),
  ];
}

// --- Color names ------------------------------------------------------------

const NAMED_COLORS: readonly [string, string][] = [
  ["White", "#ffffff"],
  ["Warm white", "#ece8e1"],
  ["Ivory", "#f3ecd9"],
  ["Cream", "#efe4cc"],
  ["Linen", "#e6dccb"],
  ["Greige", "#d6cfc4"],
  ["Beige", "#d8c6a8"],
  ["Sand", "#cdb891"],
  ["Mushroom", "#b5a898"],
  ["Taupe", "#a39282"],
  ["Stone", "#bdb6aa"],
  ["Light gray", "#c9c9c6"],
  ["Gray", "#9a9a98"],
  ["Warm gray", "#a39e94"],
  ["Slate", "#6b7178"],
  ["Charcoal", "#4a4a48"],
  ["Black", "#1f1f1f"],
  ["Tan", "#c19a6b"],
  ["Honey", "#b0814f"],
  ["Caramel", "#a8683a"],
  ["Oak", "#b48a5e"],
  ["Chestnut", "#7b4a2c"],
  ["Walnut", "#5c4033"],
  ["Espresso", "#3b2a20"],
  ["Mahogany", "#5f2a1e"],
  ["Terracotta", "#c98f72"],
  ["Rust", "#a5502b"],
  ["Brick red", "#8f3b2b"],
  ["Coral", "#e2826b"],
  ["Blush", "#e3c4b9"],
  ["Dusty rose", "#c99a9a"],
  ["Burgundy", "#6d2333"],
  ["Peach", "#f2c6a5"],
  ["Butter", "#f1df9f"],
  ["Mustard", "#c9a33a"],
  ["Ochre", "#c0873a"],
  ["Mint", "#cfe3d3"],
  ["Sage", "#b9c2ae"],
  ["Eucalyptus", "#8fa59a"],
  ["Olive", "#7a7a4a"],
  ["Moss", "#6b7350"],
  ["Emerald", "#2f7a5a"],
  ["Forest green", "#2f4a3a"],
  ["Sky", "#dfe7ec"],
  ["Powder blue", "#c6d7e3"],
  ["Dusty blue", "#a9b8c4"],
  ["Denim", "#5b7591"],
  ["Teal", "#2f6e74"],
  ["Navy", "#2f3c52"],
  ["Ink", "#232c3d"],
  ["Lavender", "#c7bdd6"],
  ["Mauve", "#a68595"],
  ["Plum", "#5d3a52"],
];

function toLab(hex: string): [number, number, number] {
  const { r, g, b } = hexToRgb(hex);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  const x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  const z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

const NAMED_LAB = NAMED_COLORS.map(([name, hex]) => ({ name, lab: toLab(hex) }));

/** A friendly name for any color ("Dusty rose"), for custom picks. */
export function nearestColorName(hex: string): string {
  const [l, a, b] = toLab(hex);
  let best = NAMED_LAB[0];
  let bestD = Infinity;
  for (const c of NAMED_LAB) {
    const d = (c.lab[0] - l) ** 2 + (c.lab[1] - a) ** 2 + (c.lab[2] - b) ** 2;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best.name;
}
